<template>
  <div class="articles-page">
    <div class="filter-bar">
      <div class="search-box">
        <select v-model="filter.category_id" class="category-select">
          <option :value="0">全部分类</option>
          <option v-for="cat in categoryOptions" :key="cat.id" :value="cat.id">
            {{ cat.level === 2 ? '└─ ' : '' }}{{ cat.category_name }}
          </option>
        </select>
        <select v-model="filter.status" class="status-select">
          <option :value="-1">全部状态</option>
          <option :value="0">草稿</option>
          <option :value="1">已发布</option>
          <option :value="2">下架</option>
        </select>
        <input 
          type="text" 
          v-model="searchKey" 
          placeholder="搜索文章标题..."
          class="search-input"
        />
        <button @click="search" class="search-btn">搜索</button>
      </div>
      <button @click="showAddModal = true" class="add-btn">+ 添加文章</button>
    </div>

    <div class="table-container">
      <table class="articles-table">
        <thead>
          <tr>
            <th>文章ID</th>
            <th>标题</th>
            <th>所属分类</th>
            <th>作者</th>
            <th>阅读量</th>
            <th>置顶</th>
            <th>热门</th>
            <th>状态</th>
            <th>发布时间</th>
            <th>创建时间</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="article in articles" :key="article.id">
            <td>{{ article.id }}</td>
            <td class="title-cell">{{ article.title }}</td>
            <td>{{ getCategoryName(article.category_id) }}</td>
            <td>{{ article.author || '-' }}</td>
            <td>{{ article.view_count || 0 }}</td>
            <td><span :class="'status ' + (article.is_top === 1 ? 'hot' : '')">{{ article.is_top === 1 ? '是' : '否' }}</span></td>
            <td><span :class="'status ' + (article.is_hot === 1 ? 'hot' : '')">{{ article.is_hot === 1 ? '是' : '否' }}</span></td>
            <td><span :class="'status ' + getStatusClass(article.status)">{{ getStatusText(article.status) }}</span></td>
            <td>{{ formatDate(article.publish_time) }}</td>
            <td>{{ formatDate(article.created_at) }}</td>
            <td>
              <button @click="editArticle(article)" class="action-btn edit">编辑</button>
              <button @click="toggleTop(article)" class="action-btn" :class="article.is_top === 1 ? 'disable' : 'enable'">
                {{ article.is_top === 1 ? '取消置顶' : '置顶' }}
              </button>
              <button @click="toggleStatus(article)" class="action-btn" :class="getStatusBtnClass(article.status)">
                {{ article.status === 1 ? '下架' : '发布' }}
              </button>
              <button @click="handleDeleteArticle(article)" class="action-btn delete">删除</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="pagination">
      <button :disabled="currentPage === 1" @click="currentPage--" class="page-btn">上一页</button>
      <span class="page-info">第 {{ currentPage }} / {{ totalPages }} 页</span>
      <button :disabled="currentPage === totalPages" @click="currentPage++" class="page-btn">下一页</button>
    </div>

    <div v-if="showAddModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content modal-large">
        <div class="modal-header">
          <h3>{{ editingArticle ? '编辑文章' : '添加文章' }}</h3>
          <button @click="closeModal" class="close-btn">×</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>文章标题</label>
            <input v-model="form.title" type="text" class="form-control" placeholder="请输入文章标题" />
          </div>
          <div class="form-group">
            <label>所属分类</label>
            <select v-model.number="form.category_id" class="form-control">
              <option :value="0">请选择分类</option>
              <option v-for="cat in categoryOptions" :key="cat.id" :value="cat.id">
                {{ cat.level === 2 ? '└─ ' : '' }}{{ cat.category_name }}
              </option>
            </select>
          </div>
          <div class="form-group">
            <label>作者名称 <span style="color:#e74c3c">*</span></label>
            <input v-model="form.author" type="text" class="form-control" placeholder="请输入作者名称" />
          </div>
          <div class="form-row">
            <div class="form-group form-group-half">
              <label>排序</label>
              <input v-model.number="form.sort" type="number" class="form-control" placeholder="数字越小越靠前" />
            </div>
            <div class="form-group form-group-half">
              <label>状态</label>
              <select v-model.number="form.status" class="form-control">
                <option :value="0">草稿</option>
                <option :value="1">已发布</option>
                <option :value="2">下架</option>
              </select>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group form-group-half">
              <label>是否置顶</label>
              <select v-model.number="form.is_top" class="form-control">
                <option :value="0">否</option>
                <option :value="1">是</option>
              </select>
            </div>
            <div class="form-group form-group-half">
              <label>是否热门</label>
              <select v-model.number="form.is_hot" class="form-control">
                <option :value="0">否</option>
                <option :value="1">是</option>
              </select>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group form-group-half">
              <label>定时发布时间（为空立即发布）</label>
              <input v-model="form.publish_time" type="datetime-local" class="form-control" />
            </div>
            <div class="form-group form-group-half">
              <label>过期时间（选填）</label>
              <input v-model="form.expire_time" type="datetime-local" class="form-control" />
            </div>
          </div>
          <div class="form-group">
            <label>文章内容</label>
            <div class="editor-toolbar">
              <button 
                @click="showHtmlMode = !showHtmlMode" 
                class="toolbar-btn"
                :class="{ active: showHtmlMode }"
              >
                {{ showHtmlMode ? '可视化' : 'HTML源码' }}
              </button>
            </div>
            <textarea 
              v-if="showHtmlMode" 
              v-model="form.content" 
              class="form-control content-textarea html-textarea" 
              rows="10" 
              placeholder="请输入HTML代码..."
            ></textarea>
            <div 
              v-else 
              class="form-control content-editable" 
              contenteditable="true"
              @input="onContentInput"
              placeholder="请输入可视化的文章内容..."
              v-html="form.content"
            ></div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeModal" class="btn-cancel">取消</button>
          <button @click="saveArticle" class="btn-save">保存</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getArticleList, createArticle, updateArticle, deleteArticle, getArticleCategoryList } from '@/api/articles'

export default {
  name: 'Articles',
  data() {
    return {
      searchKey: '',
      filter: {
        category_id: 0,
        status: -1
      },
      currentPage: 1,
      totalPages: 10,
      showAddModal: false,
      editingArticle: null,
      form: {
        title: '',
        category_id: 0,
        content: '',
        author: '广西南宁贞吉商务有限公司',
        sort: 100,
        is_top: 0,
        is_hot: 0,
        status: 0,
        publish_time: '',
        expire_time: ''
      },
      showHtmlMode: false,
      articles: [],
      categories: []
    }
  },
  computed: {
    categoryOptions() {
      const activeCategories = this.categories.filter(cat => cat && cat.status === 1)
      const parentCategories = activeCategories.filter(cat => cat && cat.level === 1).sort((a, b) => (a.sort || 0) - (b.sort || 0))
      const result = []
      parentCategories.forEach(parent => {
        if (!parent) return
        result.push(parent)
        const children = activeCategories.filter(cat => cat && cat.parent_id === parent.id).sort((a, b) => (a.sort || 0) - (b.sort || 0))
        children.forEach(child => {
          if (!child) return
          result.push(child)
        })
      })
      return result
    }
  },
  mounted() {
    this.loadCategories()
    this.loadArticles()
  },
  methods: {
    async loadCategories() {
      try {
        const response = await getArticleCategoryList({ page: 1 })
        if (response && response.data) {
          const pageData = response.data
          const items = pageData.data || pageData
          this.categories = Array.isArray(items) ? items.filter(cat => cat && cat.id !== undefined) : []
        }
      } catch (error) {
      }
    },
    onContentInput(event) {
      this.form.content = event.target.innerHTML
    },
    async loadArticles() {
      try {
        const params = { page: this.currentPage }
        if (this.searchKey.trim()) {
          params.keyword = this.searchKey.trim()
        }
        if (this.filter.category_id > 0) {
          params.category_id = this.filter.category_id
        }
        if (this.filter.status >= 0) {
          params.status = this.filter.status
        }
        const response = await getArticleList(params)
        if (response && response.data) {
          const pageData = response.data
          const items = pageData.data || pageData
          this.articles = Array.isArray(items) ? items.filter(p => p && p.id !== undefined) : []
          this.totalPages = pageData.last_page || 10
        }
      } catch (error) {
      }
    },
    search() {
      this.currentPage = 1
      this.loadArticles()
    },
    getCategoryName(categoryId) {
      if (categoryId === 0) return '未分类'
      const category = this.categories.find(cat => cat && cat.id === categoryId)
      return category ? category.category_name : '未知分类'
    },
    getStatusText(status) {
      const statusMap = {
        0: '草稿',
        1: '已发布',
        2: '下架'
      }
      return statusMap[status] || '未知'
    },
    getStatusClass(status) {
      const classMap = {
        0: 'draft',
        1: 'published',
        2: 'offline'
      }
      return classMap[status] || ''
    },
    getStatusBtnClass(status) {
      return status === 1 ? 'disable' : 'enable'
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
    },
    editArticle(article) {
      this.editingArticle = article
      this.form.title = article.title
      this.form.category_id = article.category_id || 0
      this.form.content = article.content || ''
      this.form.author = article.author || ''
      this.form.sort = article.sort || 100
      this.form.is_top = article.is_top || 0
      this.form.is_hot = article.is_hot || 0
      this.form.status = article.status || 0
      this.form.publish_time = article.publish_time ? this.formatDateTime(article.publish_time) : ''
      this.form.expire_time = article.expire_time ? this.formatDateTime(article.expire_time) : ''
      this.showAddModal = true
    },
    formatDateTime(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return date.toISOString().slice(0, 16)
    },
    async toggleTop(article) {
      const action = article.is_top === 1 ? '取消置顶' : '置顶'
      if (confirm(`确定要${action}文章 ${article.title} 吗？`)) {
        try {
          const newTop = article.is_top === 1 ? 0 : 1
          await updateArticle(article.id, { is_top: newTop })
          article.is_top = newTop
        } catch (error) {
          alert('更新失败')
        }
      }
    },
    async toggleStatus(article) {
      const action = article.status === 1 ? '下架' : '发布'
      if (confirm(`确定要${action}文章 ${article.title} 吗？`)) {
        try {
          const newStatus = article.status === 1 ? 2 : 1
          await updateArticle(article.id, { status: newStatus })
          article.status = newStatus
        } catch (error) {
          alert('更新状态失败')
        }
      }
    },
    async handleDeleteArticle(article) {
      if (confirm(`确定要删除文章 ${article.title} 吗？`)) {
        try {
          await deleteArticle(article.id)
          this.articles = this.articles.filter(p => p.id !== article.id)
          alert('删除成功')
        } catch (error) {
          alert('删除失败，请稍后重试')
        }
      }
    },
    closeModal() {
      this.showAddModal = false
      this.editingArticle = null
      this.form = {
        title: '',
        category_id: 0,
        content: '',
        author: '广西南宁贞吉商务有限公司',
        sort: 100,
        is_top: 0,
        is_hot: 0,
        status: 0,
        publish_time: '',
        expire_time: ''
      }
    },
    async saveArticle() {
      if (!this.form.title.trim()) {
        alert('请输入文章标题')
        return
      }
      if (this.form.category_id === 0) {
        alert('请选择所属分类')
        return
      }

      const submitData = {
        ...this.form,
        publish_time: this.form.publish_time || null,
        expire_time: this.form.expire_time || null
      }

      try {
        if (this.editingArticle) {
          await updateArticle(this.editingArticle.id, submitData)
          Object.assign(this.editingArticle, submitData)
          alert('更新成功')
        } else {
          const response = await createArticle(submitData)
          const newArticle = response.data.data || response.data
          if (newArticle && newArticle.id !== undefined) {
            this.articles.unshift(newArticle)
          }
          alert('添加成功')
        }
        this.closeModal()
        this.loadArticles()
      } catch (error) {
        const message = error.response?.data?.message || error.message || '保存失败，请稍后重试'
        alert(message)
      }
    }
  }
}
</script>

<style scoped>
.articles-page {
  padding: 20px;
}

.filter-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 15px 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}

.search-box {
  display: flex;
  gap: 10px;
  align-items: center;
}

.category-select, .status-select {
  padding: 10px 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  min-width: 120px;
}

.search-input {
  padding: 10px 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  width: 250px;
}

.search-btn {
  padding: 10px 20px;
  background: #3498db;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.add-btn {
  padding: 10px 20px;
  background: #2ecc71;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.table-container {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  overflow: hidden;
}

.articles-table {
  width: 100%;
  border-collapse: collapse;
}

.articles-table th, .articles-table td {
  padding: 12px 14px;
  text-align: left;
  border-bottom: 1px solid #eee;
  font-size: 13px;
}

.articles-table th {
  background: #f8f9fa;
  font-weight: 600;
  color: #333;
}

.title-cell {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.status {
  padding: 4px 0;
  border-radius: 20px;
  font-size: 12px;
}

.status.published { background: #e8f5e9; color: #2ecc71; }
.status.draft { background: #fff3e0; color: #ff9800; }
.status.offline { background: #ffebee; color: #e74c3c; }
.status.hot { background: #fce4ec; color: #e91e63; }

.action-btn {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  margin-right: 5px;
}

.action-btn.edit {
  background: #3498db;
  color: #fff;
}

.action-btn.disable {
  background: #e74c3c;
  color: #fff;
}

.action-btn.enable {
  background: #2ecc71;
  color: #fff;
}

.action-btn.delete {
  background: #95a5a6;
  color: #fff;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin-top: 20px;
}

.page-btn {
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-size: 14px;
  color: #666;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-content {
  background: #fff;
  border-radius: 8px;
  width: 500px;
  max-width: 90%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-large {
  width: 700px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
}

.modal-header h3 {
  margin: 0;
  font-size: 16px;
}

.close-btn {
  font-size: 24px;
  background: none;
  border: none;
  cursor: pointer;
  color: #999;
}

.modal-body {
  padding: 20px;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  color: #333;
}

.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}

.content-textarea {
  font-family: inherit;
  resize: vertical;
}

.form-row {
  display: flex;
  gap: 15px;
}

.form-group-half {
  flex: 1;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 15px 20px;
  border-top: 1px solid #eee;
}

.btn-cancel {
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
}

.btn-save {
  padding: 8px 16px;
  background: #3498db;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.editor-toolbar {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}

.toolbar-btn {
  padding: 6px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #f8f9fa;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.3s;
}

.toolbar-btn:hover {
  background: #e9ecef;
}

.toolbar-btn.active {
  background: #3498db;
  color: #fff;
  border-color: #3498db;
}

.content-editable {
  min-height: 200px;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
  outline: none;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: inherit;
}

.content-editable:empty:before {
  content: attr(placeholder);
  color: #999;
  pointer-events: none;
}

.content-editable:focus {
  border-color: #3498db;
}

.html-textarea {
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 12px;
}
</style>